/* Which battle at a site the battle modal is about, and the rounds of it that can still be read.

   A site hosts many battles over a game — 158-94 in game 3834 was fought over on rounds 4–5, 6,
   8, 20 and 21 — and every one of them numbers its own rounds from 1. The modal used to pour the
   `battle` event of every stored step into one map keyed by that number, so five different
   "round 1"s overwrote each other and the newest won: a replay standing on round 5, stepped back
   to the battle's first round, showed round 21's 671 zombies against a horde of a hundred
   (owner, 2026-09-22). A battle is a site AND the game round it is anchored on — the round it
   closed on, or the round it is burning on — and its round k lives on exactly one step,
   `anchor − rounds + k`. Nothing outside that span is this battle. */

const at = (col, row) => (event) =>
  event?.coordinates?.[0] === Number(col) && event?.coordinates?.[1] === Number(row)

const newest = (events, type, col, row) =>
  [...(events || [])].reverse().find((event) => event?.type === type && at(col, row)(event)) || null

/* The battle the modal is about: `{ tick, fought, rounds, endedEvent }`, or null when nothing at the site
   can be read yet. `tick` is the game round it closed on, or is burning on, and `rounds` how
   many it has; `endedEvent` is the closing report when it has one.

   In order: a shared report line names the round it closed on (`tick`), and the step of that
   round is the authority; else the live payload's own closing at the site; else the battle
   burning here now (`battles[key]`, or the live `battle` event's round); else the newest closing
   in the stored steps AT OR BEFORE the round shown — a replay standing on round 5 is never about
   a battle that closed on round 21.

   `fought` is the game round its last round was fought on, and is what the span is counted back
   from. It is `tick` unless the battle closed with no round fought on its closing round — a
   missile wiped the site before the armies moved, a side starved or walked away — which the
   closing report says by carrying no `last_round`. Its rounds then end on the round before (a
   burning battle fights every round), and counting back from `tick` put every one of them
   outside the span: game 171's 43-20, closed by two missiles on round 18, read "that round's
   report is no longer available" (owner, 2026-10-10). */
const closed = (tick, endedEvent) => {
  const rounds = Number(endedEvent.detail?.rounds ?? 1)
  const fought = endedEvent.detail?.last_round ? tick : tick - 1
  return { tick, fought, rounds, endedEvent }
}

export const anchorFor = ({ game, steps, col, row, tick = null }) => {
  const stored = [...(steps || [])].sort((a, b) => a.tick - b.tick)
  if (tick != null) {
    const step = stored.find((entry) => entry.tick === Number(tick))
    const endedEvent = newest(step?.metadata?.events, "battle_ended", col, row)
    return endedEvent ? closed(Number(tick), endedEvent) : null
  }
  const live = game?.metadata?.events || []
  const endedHere = newest(live, "battle_ended", col, row)
  if (endedHere) return closed(Number(game.tick), endedHere)
  const burning = newest(live, "battle", col, row)
  const rounds = Number(burning?.detail?.round ?? game?.metadata?.battles?.[`${col}-${row}`] ?? 0)
  if (rounds > 0) return { tick: Number(game.tick), fought: Number(game.tick), rounds, endedEvent: null }
  for (const step of [...stored].reverse()) {
    if (game?.tick != null && step.tick > Number(game.tick)) continue
    const endedEvent = newest(step.metadata?.events, "battle_ended", col, row)
    if (endedEvent) return closed(step.tick, endedEvent)
  }
  return null
}

/* Every round of the anchored battle we can read, `{ [round]: battleEvent }`: the stored step of
   each round in the battle's span, plus the live payload's own `battle` event when it is one of
   them. A step's event only counts when its round number is the one the span says it should be,
   so a different battle at the same site can never slip in. */
export const historyFor = ({ anchor, game, steps, col, row }) => {
  const history = {}
  if (!anchor) return history
  const last = anchor.fought ?? anchor.tick
  const first = last - anchor.rounds
  ;(steps || []).forEach((step) => {
    if (!(step.tick > first && step.tick <= last)) return
    const event = newest(step.metadata?.events, "battle", col, row)
    const round = step.tick - first
    if (event && Number(event.detail?.round) === round) history[round] = event
  })
  if (game?.tick != null && Number(game.tick) > first && Number(game.tick) <= last) {
    const event = newest(game.metadata?.events, "battle", col, row)
    const round = Number(game.tick) - first
    if (event && Number(event.detail?.round) === round) history[round] = event
  }
  return history
}
